import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { FloatingButton } from "src/domain/HotelsScreen/FloatingButton/FloatingButton";
import { useHotelsContext } from "src/domain/HotelsScreen/HotelsContext";
import { useTripList } from "src/domain/TripPlanner/hooks/useTripList";
import { useIsTripCreated } from "src/domain/TripPlanner/hooks/useTripURL";
import MapSolid from "src/design-system/icons/MapSolid.svg?react";
import List from "src/design-system/icons/List.svg?react";
import { useNavigateToHotelsPage } from "src/utils/hooks/useNavigateToHotelsPage";
import useUser from "src/utils/hooks/useUser";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import Search from "src/design-system/icons/Search.svg?react";
import Hotel from "src/design-system/icons/Bed.svg?react";
import Suitcase from "src/design-system/icons/Suitcase.svg?react";
import Profile from "src/design-system/icons/Profile.svg?react";
import { Button } from "react-aria-components";
import { tw } from "src/design-system/tw";
import type { MobileTabs } from "../../App";
import { useRouteCanonical, useRouteIndex } from "../../utils/hooks/useRoute";
import { useRouteSegmentIndex } from "../../utils/hooks/useSegment";
import { useTypedLocation } from "../../utils/hooks/useTypedLocation";
import { navigateToNewState } from "../../utils/location/navigateToNewState";
import { navigateToNewStateHash } from "../../utils/location/navigateToNewStateHash";
import messages from "./BottomNavbar.messages";

type Props = {
  active: MobileTabs;
  setScrollTop: (top: number) => void;
};

export function BottomNavbar({ active: highlightedTab, setScrollTop }: Props) {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useTypedLocation();
  const routeIndex = useRouteIndex();
  const routeCanonical = useRouteCanonical();
  const segmentIndex = useRouteSegmentIndex();
  const { navigateToHotels } = useNavigateToHotelsPage();
  const { showMap, setShowMap, targetPinHotelId } = useHotelsContext();
  const { user } = useUser();
  const { data } = useTripList();
  const [tripsCount, setTripsCount] = useState(0);
  const isTripCreated = useIsTripCreated();
  const isUserLoggedIn = !!user?.id;

  const handleHotelsClick = () => {
    setScrollTop(0);
    sendAnalyticsInteractionEvent({
      category: "BottomNav",
      action: "Click:Hotels",
    });
    navigateToHotels();
  };

  const handleSearchClick = () => {
    // On switch of tab, reset scroll to top
    if (showMap === true) setShowMap(false);
    setScrollTop(0);
    let navDestination;

    if (highlightedTab !== "search") {
      const routePart = routeIndex !== undefined ? `#r/${routeCanonical}` : "";
      const segmentPart =
        routeIndex !== undefined && segmentIndex !== undefined
          ? `/s/${segmentIndex}`
          : "";
      navDestination = `${routePart}${segmentPart}`;

      sendAnalyticsInteractionEvent({
        category: "NavigateHome",
        action: "Click:Search",
      });
    }

    navigateToNewStateHash(
      navigate,
      { highlightedTab: "search" },
      navDestination,
      location,
    );

    sendAnalyticsInteractionEvent({
      category: "BottomNav",
      action: "Click:Search",
    });
  };

  const handlePreferencesClick = () => {
    // On switch of tab, reset scroll to top
    if (showMap === true) setShowMap(false);
    setScrollTop(0);

    navigateToNewState(
      navigate,
      { highlightedTab: "preferences", preferencesScreen: "main" },
      location,
    );

    sendAnalyticsInteractionEvent({
      category: "BottomNav",
      action: "Click:Profile",
    });
  };

  const handleTripClick = () => {
    // On switch of tab, reset scroll to top
    if (showMap === true) setShowMap(false);
    setScrollTop(0);

    let tripURL = "#trips";

    if (!isTripCreated) {
      tripURL = `#trips/${isUserLoggedIn ? "saved" : "create"}`;
    }

    navigateToNewStateHash(
      navigate,
      { highlightedTab: "trips" },
      tripURL,
      location,
    );

    sendAnalyticsInteractionEvent({
      category: "BottomNav",
      action: "Click:Trips",
    });
  };

  const handleFloatingMapListClick = () => {
    sendAnalyticsInteractionEvent({
      category: "Hotels",
      action: "Click:ViewToggle",
      label: `Show ${showMap ? "List" : "Map"}`,
    });
    setShowMap((prev) => !prev);
  };

  useEffect(() => {
    if (isUserLoggedIn && data?.length) {
      setTripsCount(data.length);
    }
  }, [data, intl, isUserLoggedIn]);

  // The switch button overlaps with the mobile map card, so we hide it until the user dismisses the card
  const isLookingAtMapCard =
    showMap && targetPinHotelId.focusedId !== undefined;
  const showHotelSwitchButton =
    highlightedTab === "hotels" && !isLookingAtMapCard;

  return (
    <div className="fixed bottom-0 z-50 w-full">
      {showHotelSwitchButton ? (
        <FloatingButton
          leftIcon={
            showMap ? (
              <List className="h-4 text-text-on-bg-fill" />
            ) : (
              <MapSolid className="h-4 text-icon-on-bg-fill" />
            )
          }
          onClick={handleFloatingMapListClick}
          text={intl.formatMessage(showMap ? messages.list : messages.map)}
        />
      ) : null}

      <nav className="flex justify-around bg-white pb-safe text-label-md shadow-md">
        <BottomNavbarOption
          isActive={highlightedTab === "search"}
          onClick={handleSearchClick}
          text={intl.formatMessage(messages.search)}
          Svg={Search}
          optionVariant="search"
        />
        <BottomNavbarOption
          isActive={highlightedTab === "hotels"}
          onClick={handleHotelsClick}
          text={intl.formatMessage(messages.hotels)}
          Svg={Hotel}
        />
        <BottomNavbarOption
          isActive={highlightedTab === "trips"}
          onClick={handleTripClick}
          text={intl.formatMessage(messages.trips)}
          badgeCount={tripsCount}
          Svg={Suitcase}
        />
        <BottomNavbarOption
          isActive={highlightedTab === "preferences"}
          onClick={handlePreferencesClick}
          text={intl.formatMessage(messages.profile)}
          Svg={Profile}
        />
      </nav>
    </div>
  );
}

type BottomNavbarOptionProps = {
  isActive: boolean;
  onClick: () => void;
  text: string;
  Svg: React.ComponentType<{
    title?: string;
    className?: string;
  }>;
  optionVariant?: string;
  badgeCount?: number;
};

export function BottomNavbarOption({
  isActive,
  onClick,
  text,
  Svg,
  optionVariant,
  badgeCount,
}: BottomNavbarOptionProps) {
  const hasBadge = badgeCount && badgeCount > 0;

  return (
    <Button
      className={tw(
        "relative flex w-16.5 flex-col items-center px-4 pt-2 pb-[5px] text-body-sm leading-none font-medium",
        isActive && "text-text-brand",
      )}
      onClick={onClick}
      data-testid={optionVariant ? `${optionVariant}NavBar` : null}
    >
      <div className="mx-auto mt-0 mb-1">
        <Svg
          aria-hidden="true"
          className={tw(
            "-mb-1 h-7 w-7 text-neutral-1600",
            isActive && "text-text-brand",
          )}
        />
        {hasBadge ? (
          <div className="absolute top-1.5 right-1.5 rounded-lg bg-fill-brand px-1.5 py-px leading-none text-white">
            {badgeCount}
          </div>
        ) : null}
      </div>
      {text}
    </Button>
  );
}
